import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import FormSuccess from "@/components/FormSuccess";
import { usePageMeta, useJsonLd } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const TRIP_TYPES = [
  { value: "one_way", label: "One way", offerName: "One-Way Flight Booking" },
  { value: "return", label: "Return", offerName: "Return Flight Booking" },
];

const FLIGHTS_SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": "https://makchester.com/flights#service",
  name: "Flight Booking Services",
  serviceType: "International Flight Booking",
  url: "https://makchester.com/flights",
  description: "Makchester Tours & Travel provides international and domestic flight booking services for travellers worldwide.",
  provider: { "@id": "https://makchester.com/#travelagency" },
  areaServed: [
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "Country", name: "Zimbabwe" },
    { "@type": "Country", name: "South Africa" },
    "Worldwide",
  ],
  availableChannel: {
    "@type": "ServiceChannel",
    serviceUrl: "https://makchester.com",
    servicePhone: { "@type": "ContactPoint", telephone: "+971559209503", contactType: "customer service" },
  },
  offers: TRIP_TYPES.map((t) => ({
    "@type": "Offer",
    name: t.offerName,
    itemOffered: { "@type": "Service", name: t.offerName, provider: { "@id": "https://makchester.com/#travelagency" } },
  })),
};

export default function FlightForm() {
  usePageMeta("Flight Enquiry", "Request flight quotes worldwide — competitive fares on international routes with expert support from Makchester Tours & Travel.");
  useJsonLd("ld-service", FLIGHTS_SERVICE_SCHEMA);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    full_name: "", phone: "", departure_city: "", destination: sp.get("destination") || "",
    travel_date: sp.get("departure") || "", return_date: sp.get("return") || "", trip_type: "return",
    num_passengers: parseInt(sp.get("travellers")) || 1, budget: sp.get("budget") || "", notes: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/flight", form);
      trackEvent("enquiry_submitted", { form: "flight" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const reset = () => { setDone(false); setForm({ full_name: "", phone: "", departure_city: "", destination: "", travel_date: "", return_date: "", trip_type: "return", num_passengers: 1, budget: "", notes: "" }); };

  return (
    <div className="bg-white">
      <section className="bg-[#0A2E4D] text-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <span className="mk-gold-line mb-6 block" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4A62A] font-bold mb-3">Flight Request</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl">Book your flight</h1>
          <p className="text-slate-300 mt-4 max-w-xl">Tell us your route and budget — we&apos;ll find the best fare.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          {done ? (
            <FormSuccess waMessage="Hello Makchester Tours & Travel, I would like assistance with flight booking." onReset={reset} testId="flight-form-success" />
          ) : (
            <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-8 space-y-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="flight-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" required><Input data-testid="flight-full-name" value={form.full_name} onChange={set("full_name")} required /></Field>
                <Field label="Phone number" required><Input data-testid="flight-phone" value={form.phone} onChange={set("phone")} required /></Field>
                <Field label="Departure city" required><Input data-testid="flight-departure" value={form.departure_city} onChange={set("departure_city")} required /></Field>
                <Field label="Destination" required><Input data-testid="flight-destination" value={form.destination} onChange={set("destination")} required /></Field>
                <Field label="Travel date" required><Input type="date" data-testid="flight-travel-date" value={form.travel_date} onChange={set("travel_date")} required /></Field>
                <Field label="Return date"><Input type="date" data-testid="flight-return-date" value={form.return_date} onChange={set("return_date")} /></Field>
                <Field label="Trip type">
                  <RadioGroup value={form.trip_type} onValueChange={(v) => setForm({ ...form, trip_type: v })} className="flex gap-4 mt-2">
                    {TRIP_TYPES.map((t) => (
                      <div key={t.value} className="flex items-center gap-2">
                        <RadioGroupItem value={t.value} id={`rt-${t.value}`} data-testid={`flight-trip-${t.value === "one_way" ? "one" : "return"}`} />
                        <Label htmlFor={`rt-${t.value}`} className="text-sm">{t.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </Field>
                <Field label="Number of passengers"><Input type="number" min={1} data-testid="flight-passengers" value={form.num_passengers} onChange={set("num_passengers")} /></Field>
                <Field label="Budget (USD)"><Input type="number" min={0} placeholder="e.g. 800" data-testid="flight-budget" value={form.budget} onChange={set("budget")} /></Field>
              </div>
              <Field label="Notes"><Textarea rows={3} data-testid="flight-notes" value={form.notes} onChange={set("notes")} /></Field>
              <button type="submit" disabled={busy}
                      className="w-full mk-btn-gold !py-4 disabled:opacity-60" data-testid="flight-submit-btn">
                {busy ? "Submitting…" : "Get My Best Fare"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#475569] font-semibold">{label}{required && <span className="text-[#EF4444] ml-1">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
